import { useState } from 'react';
import {
  Box,
  Divider,
  Flex,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Text,
  Tooltip,
} from '@chakra-ui/react';
import { withLDConsumer } from 'launchdarkly-react-client-sdk';
import { useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';

import {
  FetchBlockOutputsV2,
  UpdateIndicator,
  UpdateIndicatorFormat,
  getBaseScenario,
  getIsCalculationSelected,
  getPlannerBlock,
  getSelectedCell,
  getSelectedIndicatorType,
  getSelectedIndicators,
  getToggleInputPanel,
  handleToggleInputPanel,
} from 'redux/PlannerModeSlice';
import { buildFetchBlockOutputsV2Payload } from 'pages/BuilderMode/dataTableV2/buildFetchBlockOutputsV2Payload';
import { capitalizeFirstLetter } from 'utils/GlobalHelpers';
import {
  accent,
  controlHeight,
  font,
  fontSize,
  neutral,
  radius,
  shadow,
  text,
} from 'styles/revampTokens';

import DraftJSFormula from '../DraftJSFormula';
import DraftJSFormula2 from '../DraftJSFormula2';
import ModernIndicatorIcon from '../icons/ModernIndicatorIcon';
import FigmaSmallChevronIcon from '../icons/FigmaSmallChevronIcon';
import FigmaSidePanelIcon from '../icons/FigmaSidePanelIcon';

const INDICATOR_TYPES = [
  { name: 'Calculation', value: 'calculation' },
  { name: 'Input', value: 'input' },
];

const DATA_TYPES = [
  { name: 'Number', value: 'number' },
  { name: 'Statistic', value: 'statistic' },
  { name: 'Percentage', value: 'percentage' },
  { name: 'Ratio', value: 'ratio' },
  { name: 'Balance', value: 'balance' },
  { name: 'Opening balance', value: 'opening_balance' },
];

const DATA_FORMATS = [
  { type: 'Number', label: '.00', value: 'number' },
  { type: 'Integer', label: '123', value: 'integer' },
  { type: 'Percentage', label: '%', value: 'percentage' },
  { type: 'Currency', label: '$', value: 'currency' },
];

const Divide = () => (
  <Divider orientation='vertical' borderColor={neutral.borderSubtle} h='18px' alignSelf='center' />
);

/** Shared look for the two dropdown triggers — ghost by default, tinted on hover/open. */
const triggerStyles = {
  // Explicit inline-flex + centring: `as={Flex}` on MenuButton doesn't
  // guarantee either, and the icon, label and chevron were sitting on
  // different baselines.
  display: 'inline-flex',
  // Chakra's MenuButton renders a button, and `as={Flex}` doesn't reliably
  // carry the row direction through — the icon, label and chevron were
  // stacking vertically instead of sitting on one line.
  flexDirection: 'row',
  flexWrap: 'nowrap',
  alignItems: 'center',
  whiteSpace: 'nowrap',
  h: '28px',
  px: '8px',
  borderRadius: radius.control,
  bg: 'transparent',
  cursor: 'pointer',
  flexShrink: 0,
  transition: 'background 0.12s ease',
  _hover: { bg: neutral.surfaceHover },
  _expanded: { bg: accent.subtle },
  // `as const` keeps the literals narrow — spread into MenuButton, a widened
  // `string` doesn't satisfy Chakra's style unions.
} as const;

/** Panel + row styling for both dropdowns, so they read as part of the revamp
    chrome rather than Chakra's stock menu: tighter rows, 13px body text, and
    the accent reserved for the current selection. */
const menuListStyles = {
  minW: '160px',
  py: '4px',
  bg: neutral.surface,
  border: '1px solid',
  borderColor: neutral.border,
  borderRadius: radius.surface,
  boxShadow: shadow.panel,
} as const;

const menuItemStyles = {
  h: '32px',
  px: '10px',
  fontFamily: font.body,
  fontSize: fontSize.body,
  fontWeight: 500,
  color: text.primary,
  borderRadius: radius.control,
  transition: 'background 0.12s ease',
  _hover: { bg: neutral.surfaceHover },
  _focus: { bg: neutral.surfaceHover },
} as const;

const selectedItemStyles = {
  bg: accent.subtle,
  color: accent.hover,
} as const;

/**
 * The earlier passes gave every control its own filled box, so the bar read as
 * a row of competing chips with no hierarchy. This one keeps a single elevated
 * white surface and makes the controls ghost — the formula field is the only
 * bordered element, since it's the one you actually type into. Purple is
 * reserved for active/open state rather than sitting on the idle type pill,
 * matching the rest of the revamp's neutral chrome.
 */
const FormulaBar = ({ flags }: any) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const { blockId } = useParams();
  const dispatch: any = useDispatch();
  const baseScenario = useSelector(getBaseScenario);
  const plannerBlock = useSelector(getPlannerBlock);
  const toggleInputPanel = useSelector(getToggleInputPanel);
  const selectedIndicators: any = useSelector(getSelectedIndicators);
  const indicatorType = useSelector(getSelectedIndicatorType);
  const isCalculation = useSelector(getIsCalculationSelected);
  const selectedCell: any = useSelector(getSelectedCell);
  const selectedCellReference = selectedCell?.cellReference ?? '';
  const selectedCellIndicatorName = selectedCell?.cellIndicatorName ?? '';

  const activeFormat = capitalizeFirstLetter(selectedIndicators?.format ?? '');
  const activeDataType = capitalizeFirstLetter(selectedIndicators?.data_type ?? '');

  const refetchIndicatorData = () => {
    if (!baseScenario?.id) return;
    dispatch(
      FetchBlockOutputsV2(
        buildFetchBlockOutputsV2Payload({
          blockId,
          plannerBlock,
          scenarioId: baseScenario.id,
          useArrowResponse: Boolean(flags?.builderArrowOutputs),
        }),
      ),
    );
  };

  const handleFieldChange = (key: string, value: any) => {
    dispatch(UpdateIndicator({ indicatorId: selectedIndicators?.id, data: { [key]: value } })).then(
      () => refetchIndicatorData(),
    );
  };

  const handleFormatChange = (key: string, value: any) => {
    dispatch(
      UpdateIndicatorFormat({ indicatorId: selectedIndicators?.id, data: { [key]: value } }),
    ).then(() => refetchIndicatorData());
  };

  // The bar stays mounted at all times (Excel-style) instead of disappearing
  // when nothing is selected — it previously unmounted whenever
  // selectedIndicators was falsy, which is exactly the "not showing when I
  // want it" complaint. With nothing selected we just show a quiet
  // placeholder instead of the live controls.
  if (!selectedIndicators) {
    return (
      <Flex
        alignItems='center'
        gap='8px'
        bg={neutral.surface}
        border='1px solid'
        borderColor={neutral.border}
        borderRadius={radius.surface}
        boxShadow={shadow.panel}
        px='10px'
        py='6px'
        mb='12px'
        h='40px'
      >
        <Text fontFamily={font.body} fontSize={fontSize.body} color={text.secondary}>
          Select a row or cell to see its details here
        </Text>
      </Flex>
    );
  }

  return (
    <Flex
      alignItems='center'
      gap='8px'
      bg={neutral.surface}
      border='1px solid'
      borderColor={neutral.border}
      borderRadius={radius.surface}
      boxShadow={shadow.panel}
      px='8px'
      py='6px'
      mb='12px'
    >
      {/* Excel's name box — the reference of the focused cell (top-left of a
          range, since that's where ReactGrid anchors focus) plus the
          indicator the toolbar is acting on, so it's unambiguous which row
          the type/format controls to the right will change. */}
      <Flex
        alignItems='center'
        gap='6px'
        h={controlHeight}
        minW='92px'
        maxW='220px'
        px='8px'
        flexShrink={0}
        bg={neutral.surfaceHover}
        border='1px solid'
        borderColor={neutral.border}
        borderRadius={radius.control}
        overflow='hidden'
      >
        <Text
          fontFamily={font.display}
          fontSize={fontSize.label}
          fontWeight={700}
          color={selectedCellReference ? accent.solid : text.muted}
          flexShrink={0}
        >
          {selectedCellReference || '—'}
        </Text>
        {selectedCellIndicatorName ? (
          <Text
            fontFamily={font.body}
            fontSize={fontSize.label}
            color={text.secondary}
            overflow='hidden'
            textOverflow='ellipsis'
            whiteSpace='nowrap'
            title={selectedCellIndicatorName}
          >
            {selectedCellIndicatorName}
          </Text>
        ) : null}
      </Flex>

      <Divide />

      <Menu>
        {/* The row lives in an inner Flex we own. Setting direction on
            MenuButton itself didn't hold — its own styles won, and the icon,
            label and chevron stacked. */}
        <MenuButton as={Box} {...triggerStyles}>
          <Flex direction='row' align='center' gap='6px' wrap='nowrap' whiteSpace='nowrap'>
            <Box as='span' display='flex' alignItems='center' flexShrink={0}>
              <ModernIndicatorIcon indicatorType={indicatorType} size={16} />
            </Box>
            <Text
              fontFamily={font.body}
              fontSize={fontSize.body}
              fontWeight={500}
              color={text.primary}
            >
              {isCalculation ? 'Calculation' : 'Input'}
            </Text>
            <Box as='span' display='flex' alignItems='center' flexShrink={0}>
              <FigmaSmallChevronIcon width={8} height={5} color={neutral.borderStrong} />
            </Box>
          </Flex>
        </MenuButton>
        <MenuList zIndex={16} {...menuListStyles}>
          {INDICATOR_TYPES.map((item) => (
            <MenuItem
              key={item.value}
              {...menuItemStyles}
              {...(indicatorType === item.value ? selectedItemStyles : {})}
              onClick={() => handleFieldChange('type', item.value)}
            >
              <Flex align='center' gap='8px'>
                <ModernIndicatorIcon indicatorType={item.value} size={16} />
                {item.name}
              </Flex>
            </MenuItem>
          ))}
        </MenuList>
      </Menu>

      <Divide />

      {/* The formula field is the one element that carries a border — it's the
          editable surface, so everything else stays ghost to keep it the focus. */}
      {isCalculation ? (
        <Flex
          flex='1'
          minW={0}
          h={controlHeight}
          alignItems='center'
          gap='8px'
          bg={neutral.surface}
          border='1px solid'
          borderColor={neutral.border}
          borderRadius={radius.control}
          px='10px'
          overflow='hidden'
          transition='border-color 0.12s ease, box-shadow 0.12s ease'
          _focusWithin={{ borderColor: accent.solid, boxShadow: `0 0 0 3px ${accent.subtle}` }}
          className='revamp-formula-field'
        >
          <Text
            fontFamily={font.display}
            fontSize={fontSize.label}
            fontStyle='italic'
            fontWeight={700}
            color={neutral.borderStrong}
            flexShrink={0}
            userSelect='none'
          >
            fx
          </Text>
          <Box flex='1' minW={0} display='flex' alignItems='center'>
            {flags?.draftJsFormulaBar ? (
              <DraftJSFormula2 setIsExpanded={setIsExpanded} isExpanded={isExpanded} />
            ) : (
              <DraftJSFormula setIsExpanded={setIsExpanded} isExpanded={isExpanded} />
            )}
          </Box>
        </Flex>
      ) : (
        <Flex flex='1' minW={0} alignItems='center'>
          {/* A bare text link read as body copy rather than something to
              click. A bordered control with the panel glyph matches the other
              toolbar controls and shows its on/off state through the accent. */}
          <Flex
            as='button'
            type='button'
            h='28px'
            alignItems='center'
            gap='6px'
            bg={toggleInputPanel ? accent.subtle : neutral.surface}
            border='1px solid'
            borderColor={toggleInputPanel ? accent.border : neutral.border}
            borderRadius={radius.control}
            px='10px'
            cursor='pointer'
            transition='background 0.12s ease, border-color 0.12s ease'
            _hover={{
              bg: toggleInputPanel ? accent.subtle : neutral.surfaceHover,
              borderColor: toggleInputPanel ? accent.border : neutral.borderStrong,
            }}
            onClick={() => dispatch(handleToggleInputPanel())}
          >
            <FigmaSidePanelIcon
              color={toggleInputPanel ? accent.hover : text.secondary}
              width={13}
              height={13}
            />
            <Text
              fontFamily={font.body}
              fontSize={fontSize.body}
              fontWeight={500}
              color={toggleInputPanel ? accent.hover : text.secondary}
              whiteSpace='nowrap'
            >
              {toggleInputPanel ? 'Hide input panel' : 'Show input panel'}
            </Text>
          </Flex>
        </Flex>
      )}

      <Divide />

      <Menu>
        <MenuButton as={Box} {...triggerStyles}>
          <Flex direction='row' align='center' gap='6px' wrap='nowrap' whiteSpace='nowrap'>
            <Text
              fontFamily={font.body}
              fontSize={fontSize.body}
              fontWeight={500}
              color={text.primary}
              lineHeight='1'
              whiteSpace='nowrap'
            >
              {activeDataType || 'Number'}
            </Text>
            <Box as='span' display='flex' alignItems='center' flexShrink={0}>
              <FigmaSmallChevronIcon width={8} height={5} color={neutral.borderStrong} />
            </Box>
          </Flex>
        </MenuButton>
        <MenuList zIndex={16} {...menuListStyles}>
          {DATA_TYPES.map((item) => (
            <MenuItem
              key={item.value}
              {...menuItemStyles}
              {...(activeDataType === item.name ? selectedItemStyles : {})}
              onClick={() => handleFieldChange('data_type', item.value)}
            >
              {item.name}
            </MenuItem>
          ))}
        </MenuList>
      </Menu>

      {/* Segmented control on a recessed track: one grey trough, and the
          active segment reads as a raised white chip inside it. The format
          name moved into a tooltip — as a second 9px line under each glyph it
          was the main source of clutter in a 4-up row this narrow. */}
      <Flex
        bg={neutral.surfaceMuted}
        border='1px solid'
        borderColor={neutral.borderSubtle}
        borderRadius={radius.control}
        p='2px'
        gap='2px'
        flexShrink={0}
      >
        {DATA_FORMATS.map((item) => {
          const isActive = activeFormat === item.type;
          return (
            <Tooltip key={item.value} label={item.type} placement='top' openDelay={400}>
              <Flex
                as='button'
                type='button'
                alignItems='center'
                justifyContent='center'
                w='34px'
                h='24px'
                borderRadius='6px'
                bg={isActive ? accent.solid : 'transparent'}
                boxShadow={isActive ? '0 1px 2px rgba(16, 20, 30, 0.18)' : 'none'}
                cursor='pointer'
                transition='background 0.12s ease, box-shadow 0.12s ease'
                _hover={{ bg: isActive ? accent.hover : neutral.surfaceHover }}
                onClick={() => handleFormatChange('data_format', item.value)}
              >
                <Text
                  fontFamily={font.body}
                  fontSize={fontSize.body}
                  fontWeight={isActive ? 600 : 500}
                  color={isActive ? text.inverse : text.secondary}
                >
                  {item.label}
                </Text>
              </Flex>
            </Tooltip>
          );
        })}
      </Flex>
    </Flex>
  );
};

export default withLDConsumer()(FormulaBar);
